// Backend service for sending invitation emails
import { API_BASE_URL } from "@/config";
import { getAccessToken } from "@/lib/auth-utils";

export interface FriendInviteEmailData {
  friendName: string;
  inviterName: string;
  inviteCode: string;
  shareUrl: string;
  dailyLimit: number;
  trialDays: number;
  expiresAt: string;
}

export class ResendService {
  static async sendFriendInviteEmail(
    friendEmail: string,
    data: FriendInviteEmailData
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      const requestData = {
        friendEmail,
        ...data,
      };

      // Sending friend invitation email via Python backend

      // Get the access token for authentication
      const token = await getAccessToken();

      // Use the Python backend endpoint
      const response = await fetch(`${API_BASE_URL}/api/friend-invite/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Backend API error:", errorData);
        return {
          success: false,
          error:
            errorData.message ||
            `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const result = await response.json();
      // Friend invitation email sent successfully
      return {
        success: true,
        messageId: result.messageId || result.id,
      };
    } catch (error) {
      console.error("Error sending friend invitation email:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async checkBackendHealth(): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Get the access token for authentication
      const token = await getAccessToken();

      const response = await fetch(`${API_BASE_URL}/api/friend-invite/health`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        return {
          success: false,
          error: `Health check failed: ${response.status} ${response.statusText}`,
        };
      }

      const result = await response.json();
      return { success: true };
    } catch (error) {
      console.error("Backend health check error:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}
